import { cn, formatMoney, formatNumber } from '@/lib/utils'
import type { components } from '@/api/schema'
import { days, sourceText } from './format'

type LeadTime = components['schemas']['QuotedVsActualLeadTime']
type Money = components['schemas']['QuotedVsActualMoney']

// A lead time this much later than quoted (and at least half the quote again) stands out.
const LATE_DAYS = 2
const LATE_SHARE = 0.5
// A Net or freight difference stands out from this much over the quote.
const OVER_AMOUNT = 25
const OVER_PERCENT = 3



const chip = 'inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium tabular-nums'
const standout = 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
const quiet = 'bg-muted text-muted-foreground'

export function DaysGap({
  gap,
  quoted,
  label,
}: {
  gap: number | null | undefined
  quoted: number | null | undefined
  /** Shown instead of the gap (a PO's own days, coloured by its gap). */
  label?: string
}) {
  if (gap == null) return label ? <span className="tabular-nums">{label}</span> : <span className="text-muted-foreground">—</span>
  const late = gap >= LATE_DAYS && gap >= (quoted ?? 0) * LATE_SHARE
  const text = label ?? (gap === 0 ? 'On time' : `${gap > 0 ? '+' : '−'}${days(Math.abs(gap))}`)
  return <span className={cn(chip, late ? standout : quiet)}>{text}</span>
}

export function MoneyGap({ money }: { money: Money }) {
  const difference = Number(money.difference)
  const percent = money.difference_percent == null ? null : Number(money.difference_percent)
  if (difference === 0) return <span className={cn(chip, quiet)}>Same</span>
  const over = difference >= OVER_AMOUNT && (percent == null || percent >= OVER_PERCENT)
  const sign = difference > 0 ? '+' : '−'
  return (
    <span className={cn(chip, over ? standout : quiet)}>
      {sign}
      {formatMoney(Math.abs(difference))}
      {percent != null && ` · ${sign}${formatNumber(Math.abs(percent), 1)}%`}
    </span>
  )
}


export function SourceLabel({ lead, className }: { lead: LeadTime; className?: string }) {
  const text = sourceText(lead)
  if (!text) return null
  return <p className={cn('flex text-xs text-muted-foreground', className)}>{text}</p>
}
